/**
 * Text chat.
 *
 * Kate, twice, unprompted: "Text chat." — "What about mic chat?" — "there's no mic
 * chat... Let's say just text chat. Just text."
 *
 * So this is text only. There is deliberately no microphone permission request, no
 * WebRTC audio, and no voice code anywhere in the project.
 */
export interface ChatMessage {
  from: string
  text: string
  /** Colour of the sender's map dot, so chat and map agree. */
  color: string
}

const MAX_VISIBLE = 8
const MAX_LENGTH = 120

export class Chat {
  readonly root: HTMLDivElement
  private readonly log: HTMLDivElement
  private readonly input: HTMLInputElement
  private readonly messages: ChatMessage[] = []
  /** Called when the player sends a line. */
  onSend: ((text: string) => void) | null = null
  /** True while typing, so movement keys go to the chat box and not the player. */
  get isTyping(): boolean {
    return document.activeElement === this.input
  }

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div')
    this.root.className = 'chat'

    this.log = document.createElement('div')
    this.log.className = 'chat-log'

    this.input = document.createElement('input')
    this.input.className = 'chat-input'
    this.input.type = 'text'
    this.input.maxLength = MAX_LENGTH
    // No key is named here: on a phone there is no keyboard to press T on. The desktop
    // hint lives in the start screen instead.
    this.input.placeholder = 'Say something…'
    this.input.autocomplete = 'off'

    this.input.addEventListener('keydown', (event) => {
      event.stopPropagation()
      if (event.key === 'Enter') {
        const text = this.input.value.trim()
        this.input.value = ''
        this.input.blur()
        this.root.classList.remove('is-open')
        if (text) this.onSend?.(text)
      } else if (event.key === 'Escape') {
        this.input.value = ''
        this.input.blur()
        this.root.classList.remove('is-open')
      }
    })

    this.root.append(this.log, this.input)
    parent.append(this.root)
  }

  /** Opens the box (it is hidden on touch until asked for) and puts the caret in it. */
  focus(): void {
    this.root.classList.add('is-open')
    this.input.focus()
  }

  add(message: ChatMessage): void {
    this.messages.push(message)
    if (this.messages.length > MAX_VISIBLE) this.messages.shift()
    this.render()
  }

  private render(): void {
    this.log.innerHTML = ''
    for (const message of this.messages) {
      const line = document.createElement('p')
      line.className = 'chat-line'
      const who = document.createElement('span')
      who.className = 'chat-who'
      who.style.color = message.color
      who.textContent = message.from
      const body = document.createElement('span')
      // textContent, never innerHTML — chat is player-authored text.
      body.textContent = message.text
      line.append(who, body)
      this.log.append(line)
    }
    this.log.scrollTop = this.log.scrollHeight
  }

  get history(): readonly ChatMessage[] {
    return this.messages
  }
}
